import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

// Achievement Badges CRUD — backend contract in public/achievement-badges-api.md
// Notes that shaped this slice:
//  - Base path: /achievement-badges
//  - POST and PUT ALWAYS take an array under `data`, even for a single badge.
//  - PUT carries `badge_id` (a JSON number, not a string) in the BODY, no id in the URL.
//  - PUT and DELETE return no record — callers must refetch after mutating.
//  - Standard portal envelope { status, message, data, meta }; `meta` only on list.

const initialState = {
  // list endpoint (paginated)
  table: {
    loading: false,
    data: [],
    meta: {},
  },
  // single badge (get one — includes audit columns the list omits)
  single: {
    loading: false,
    data: null,
  },
  // shared flag for create / update / delete
  mutating: false,
  // badge image upload (multipart)
  uploading: false,
  // skillx assignments for the selected course (add/edit form dropdown)
  assignments: {
    loading: false,
    data: [],
  },
};

// 1. LIST ------------------------------------------------------------------
export const getAchievementBadges = createAsyncThunk(
  'achievementBadge/getAchievementBadges',
  async (params = {}) => {
    const { page = 1, limit = 10, search = '', sortBy, sortDirection } = params;

    const searchParams = new URLSearchParams({
      page,
      limit,
      search: search ?? '',
    });
    if (sortBy) {
      searchParams.append('sortBy', sortBy);
      searchParams.append('sortDirection', sortDirection || 'asc');
    }

    const res = await apiRequest({
      method: 'get',
      url: `achievement-badges?${searchParams.toString()}`,
    });
    return res;
  },
);

// 2. GET ONE ---------------------------------------------------------------
export const getAchievementBadge = createAsyncThunk(
  'achievementBadge/getAchievementBadge',
  async (badgeId) => {
    const res = await apiRequest({
      method: 'get',
      url: `achievement-badges/${badgeId}`,
    });
    return res;
  },
);

// 3. CREATE (array payload, all-or-nothing) --------------------------------
export const createAchievementBadges = createAsyncThunk(
  'achievementBadge/createAchievementBadges',
  async (data) => {
    const res = await apiRequest({
      method: 'post',
      url: 'achievement-badges',
      data: { data: Array.isArray(data) ? data : [data] },
    });
    return res;
  },
);

// 4. UPDATE (array payload, badge_id in body as a number) ------------------
export const updateAchievementBadges = createAsyncThunk(
  'achievementBadge/updateAchievementBadges',
  async (data) => {
    const list = (Array.isArray(data) ? data : [data]).map((item) => ({
      ...item,
      badge_id: Number(item.badge_id), // API rejects a string id
    }));
    const res = await apiRequest({
      method: 'put',
      url: 'achievement-badges',
      data: { data: list },
    });
    return res;
  },
);

// 5. DELETE (soft delete, id in URL) ---------------------------------------
export const deleteAchievementBadge = createAsyncThunk(
  'achievementBadge/deleteAchievementBadge',
  async (badgeId) => {
    const res = await apiRequest({
      method: 'delete',
      url: `achievement-badges/${badgeId}`,
    });
    return res;
  },
);

// 6. UPLOAD BADGE IMAGE ----------------------------------------------------
// POST /general/upload-badge — multipart/form-data, field name `badgeImage`
// (images or PDF, max 4 MB). Returns the bare filename in data.filename;
// prepend the uploads base to display it. Pass data.filename as badge_image.
export const uploadBadgeImage = createAsyncThunk(
  'achievementBadge/uploadBadgeImage',
  async (file, action) => {
    const formData = new FormData();
    formData.append('badgeImage', file);
    const res = await apiRequest({
      method: 'post',
      url: 'general/upload-badge',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (res?.status === 'success') return res;
    return action.rejectWithValue(res);
  },
);

// 7. SKILLX ASSIGNMENTS (for the badge form dropdown) ----------------------
// GET /skillx-assignments?course_id=<id>&limit=no — assignments for a course.
// Omitting course_id returns all; an unknown course returns an empty array.
export const getSkillxAssignments = createAsyncThunk(
  'achievementBadge/getSkillxAssignments',
  async ({ courseId } = {}) => {
    const searchParams = new URLSearchParams({ limit: 'no' });
    if (courseId !== undefined && courseId !== null && courseId !== '') {
      searchParams.append('course_id', courseId);
    }
    const res = await apiRequest({
      method: 'get',
      url: `skillx-assignments?${searchParams.toString()}`,
    });
    return res;
  },
);

const achievementBadgeSlice = createSlice({
  name: 'achievementBadge',
  initialState,
  reducers: {
    clearAchievementBadge: (state) => {
      state.single = { loading: false, data: null };
    },
    clearSkillxAssignments: (state) => {
      state.assignments = { loading: false, data: [] };
    },
  },
  extraReducers: (builder) => {
    // ---------------- LIST ----------------
    builder.addCase(getAchievementBadges.pending, (state) => {
      state.table.loading = true;
    });
    builder.addCase(getAchievementBadges.fulfilled, (state, action) => {
      state.table.loading = false;
      state.table.data = action.payload?.data || [];
      state.table.meta = action.payload?.meta || {};
    });
    builder.addCase(getAchievementBadges.rejected, (state) => {
      state.table.loading = false;
    });

    // ---------------- GET ONE ----------------
    builder.addCase(getAchievementBadge.pending, (state) => {
      state.single.loading = true;
    });
    builder.addCase(getAchievementBadge.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload?.data || null;
    });
    builder.addCase(getAchievementBadge.rejected, (state) => {
      state.single.loading = false;
    });

    // ---------------- CREATE ----------------
    builder.addCase(createAchievementBadges.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(createAchievementBadges.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(createAchievementBadges.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- UPDATE ----------------
    builder.addCase(updateAchievementBadges.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(updateAchievementBadges.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(updateAchievementBadges.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- DELETE ----------------
    builder.addCase(deleteAchievementBadge.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(deleteAchievementBadge.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(deleteAchievementBadge.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- UPLOAD IMAGE ----------------
    builder.addCase(uploadBadgeImage.pending, (state) => {
      state.uploading = true;
    });
    builder.addCase(uploadBadgeImage.fulfilled, (state) => {
      state.uploading = false;
    });
    builder.addCase(uploadBadgeImage.rejected, (state) => {
      state.uploading = false;
    });

    // ---------------- SKILLX ASSIGNMENTS ----------------
    builder.addCase(getSkillxAssignments.pending, (state) => {
      state.assignments.loading = true;
    });
    builder.addCase(getSkillxAssignments.fulfilled, (state, action) => {
      state.assignments.loading = false;
      state.assignments.data = action.payload?.data || [];
    });
    builder.addCase(getSkillxAssignments.rejected, (state) => {
      state.assignments.loading = false;
      state.assignments.data = [];
    });
  },
});

export const { clearAchievementBadge, clearSkillxAssignments } = achievementBadgeSlice.actions;
export default achievementBadgeSlice.reducer;
